import { useCallback, useEffect, useMemo, useState } from "react";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import Button from "../components/UI/Button";
import Checkbox from "../components/UI/Checkbox";
import ErrorMessages from "../components/UI/ErrorMessages";
import Input from "../components/UI/Input";
import Loader from "../components/UI/Loader";
import Modal from "../components/UI/Modal";
import { DataExport, DataExportDataset, DataExportDatasetDefinition } from "../models/serviceModels/DataExport";
import {
    cancelDataExport,
    createDataExport,
    forceCancelDataExport,
    getDataExport,
    getDataExportDatasets,
    getDataExports
} from "../services/organizationService";
import {
    getIncludedExportFiles,
    getSelectedExportFileCount,
    normalizeDataExportDatasetDefinitions
} from "../utils/dataExportDatasets";
import DataTable from "../components/UI/DataTable";
import useSortableData from "../hooks/useSortableData";
import { DataTableColumn } from "../models/DataTable";
import { sortableDataExportsEnabled } from "../utils/features";
import "./ManageDataExports.css";

const ACTIVE_STATUSES = ["Pending", "InProgress", "CancelRequested"];
const DOWNLOADABLE_STATUSES = ["Completed", "CompletedWithErrors"];

const formatStatus = (status?: string | null) => {
    if (!status) {
        return "";
    }

    return status
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/_/g, " ");
};

const formatDateTime = (value?: string | null, fallback = "") => {
    if (!value) {
        return fallback;
    }

    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
        return date.toLocaleString(undefined, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "numeric",
            minute: "2-digit"
        });
    }

    return value.slice(0, 16).replace("T", " ");
};

const formatFileSize = (value?: number | null) => {
    if (value === null || value === undefined) {
        return "";
    }

    if (value < 1024) {
        return `${value} B`;
    }

    if (value < 1024 * 1024) {
        return `${(value / 1024).toFixed(1)} KB`;
    }

    return `${(value / 1024 / 1024).toFixed(1)} MB`;
};

const formatDateOnly = (value: string) => {
    const [datePart] = value.split("T");
    if (!datePart) {
        return value;
    }

    const [year, month, day] = datePart.split("-");
    return month && day && year ? `${month}/${day}/${year}` : datePart;
};

const formatDateRange = (dataExport: DataExport) => {
    if (!dataExport.dateRangeStart && !dataExport.dateRangeEnd) {
        return "All created or updated dates";
    }

    const start = dataExport.dateRangeStart ? formatDateOnly(dataExport.dateRangeStart) : "Beginning";
    const end = dataExport.dateRangeEnd ? formatDateOnly(dataExport.dateRangeEnd) : "Now";
    return `${start} to ${end}`;
};

const getCreatedBy = (dataExport: DataExport) => {
    return [dataExport.createdByFirstName, dataExport.createdByLastName].filter(Boolean).join(" ") || dataExport.createdBy;
};

const isRecentCompletedExport = (dataExport: DataExport) => {
    if (!DOWNLOADABLE_STATUSES.includes(dataExport.status) || !dataExport.completed) {
        return false;
    }

    const completed = new Date(dataExport.completed);
    if (Number.isNaN(completed.getTime())) {
        return false;
    }

    return completed >= new Date(Date.now() - 3 * 24 * 60 * 60 * 1000);
};

const ManageDataExports = () => {
    const [datasets, setDatasets] = useState<DataExportDatasetDefinition[]>([]);
    const [exports, setExports] = useState<DataExport[]>([]);
    const [selectedDatasetIds, setSelectedDatasetIds] = useState<string[]>([]);
    const [dateRangeStart, setDateRangeStart] = useState("");
    const [dateRangeEnd, setDateRangeEnd] = useState("");
    const [activeExport, setActiveExport] = useState<DataExport>();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [apiError, setApiError] = useState<any>();
    const [validationError, setValidationError] = useState<string>();
    const [confirmExportVisible, setConfirmExportVisible] = useState(false);
    const [exportDetailsVisible, setExportDetailsVisible] = useState(false);
    const [datasetDetails, setDatasetDetails] = useState<DataExportDatasetDefinition>();
    const [showRunningExports, setShowRunningExports] = useState(false);
    const [showRecentCompletedExports, setShowRecentCompletedExports] = useState(false);

    const activeExportId = activeExport?.id;
    const activeExportRunning = !!activeExport && ACTIVE_STATUSES.includes(activeExport.status);
    const selectedDatasetSet = useMemo(() => new Set(selectedDatasetIds), [selectedDatasetIds]);
    const selectedDatasets = useMemo(
        () => datasets.filter(dataset => selectedDatasetSet.has(dataset.id)),
        [datasets, selectedDatasetSet]
    );
    const selectedFileCount = useMemo(
        () => getSelectedExportFileCount(selectedDatasets),
        [selectedDatasets]
    );
    const runningExports = useMemo(() => exports.filter(dataExport => ACTIVE_STATUSES.includes(dataExport.status)), [exports]);
    const recentCompletedExports = useMemo(() => exports.filter(isRecentCompletedExport), [exports]);
    const hasRunningExports = runningExports.length > 0;
    const hasRecentCompletedExports = recentCompletedExports.length > 0;
    const getExportSortValue = useCallback((dataExport: DataExport, key: string) => {
        if (key === 'ranBy') return getCreatedBy(dataExport);
        if (key === 'files') return dataExport.selectedDatasets.length;
        return dataExport[key as keyof DataExport];
    }, []);
    const getDatasetSortValue = useCallback((dataset: DataExportDataset, key: string) => {
        if (key === 'file') return dataset.datasetName;
        return dataset[key as keyof DataExportDataset];
    }, []);
    const { data: sortedRunningExports, sortBy: runningSortBy, sortDirection: runningSortDirection, onSort: onRunningSort } = useSortableData(runningExports, getExportSortValue);
    const { data: sortedRecentExports, sortBy: recentSortBy, sortDirection: recentSortDirection, onSort: onRecentSort } = useSortableData(recentCompletedExports, getExportSortValue);
    const { data: sortedActiveDatasets, sortBy: datasetSortBy, sortDirection: datasetSortDirection, onSort: onDatasetSort } = useSortableData(activeExport?.datasets || [], getDatasetSortValue);

    useEffect(() => {
        document.title = "Data Exports";
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!activeExportId || !activeExportRunning) {
            return;
        }

        const interval = window.setInterval(() => {
            refreshExport(activeExportId);
        }, 5000);

        return () => window.clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeExportId, activeExportRunning]);

    const loadData = async () => {
        setLoading(true);
        setApiError(undefined);
        try {
            const [datasetResult, exportResult] = await Promise.all([
                getDataExportDatasets(),
                getDataExports(0, 25)
            ]);
            setDatasets(normalizeDataExportDatasetDefinitions(datasetResult));
            setExports(exportResult);
            const active = exportResult.find(dataExport => ACTIVE_STATUSES.includes(dataExport.status));
            if (active) {
                await refreshExport(active.id);
            }
        } catch (e) {
            console.error(e);
            setApiError(e);
        } finally {
            setLoading(false);
        }
    };

    const refreshExport = async (dataExportId: string, showDetails = false) => {
        try {
            const result = await getDataExport(dataExportId);
            setActiveExport(result);
            setExports(current => current.map(dataExport => dataExport.id === result.id ? result : dataExport));
            if (!ACTIVE_STATUSES.includes(result.status)) {
                const exportResult = await getDataExports(0, 25);
                setExports(exportResult);
            }
            if (showDetails) {
                setExportDetailsVisible(true);
            }
        } catch (e) {
            console.error(e);
            setApiError(e);
        }
    };

    const refreshExports = async () => {
        try {
            const exportResult = await getDataExports(0, 25);
            setExports(exportResult);
        } catch (e) {
            console.error(e);
            setApiError(e);
        }
    };

    const toggleDataset = (datasetId: string, checked: boolean) => {
        setValidationError(undefined);
        if (checked) {
            setSelectedDatasetIds([...selectedDatasetIds, datasetId]);
        } else {
            setSelectedDatasetIds(selectedDatasetIds.filter(id => id !== datasetId));
        }
    };

    const selectAllDatasets = () => {
        setValidationError(undefined);
        setSelectedDatasetIds(datasets.map(dataset => dataset.id));
    };

    const clearDatasets = () => {
        setValidationError(undefined);
        setSelectedDatasetIds([]);
    };

    const handleOpenConfirmExport = () => {
        setValidationError(undefined);
        setApiError(undefined);

        if (selectedDatasetIds.length === 0) {
            setValidationError("Select at least one dataset");
            return;
        }

        if (dateRangeStart && dateRangeEnd && dateRangeStart > dateRangeEnd) {
            setValidationError("Start date must be before end date");
            return;
        }

        setConfirmExportVisible(true);
    };

    const handleCreateExport = async () => {
        setSubmitting(true);
        try {
            const result = await createDataExport(selectedDatasetIds, dateRangeStart, dateRangeEnd);
            setConfirmExportVisible(false);
            setSelectedDatasetIds([]);
            setDateRangeStart("");
            setDateRangeEnd("");
            const fullExport = await getDataExport(result.id);
            setActiveExport(fullExport);
            const exportResult = await getDataExports(0, 25);
            setExports(exportResult);
        } catch (e) {
            console.error(e);
            setApiError(e);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelExport = async (dataExportId: string) => {
        setApiError(undefined);
        try {
            const result = await cancelDataExport(dataExportId);
            setActiveExport(await getDataExport(result.id));
            const exportResult = await getDataExports(0, 25);
            setExports(exportResult);
        } catch (e) {
            console.error(e);
            setApiError(e);
        }
    };

    const handleForceCancelExport = async (dataExportId: string) => {
        setApiError(undefined);
        try {
            const result = await forceCancelDataExport(dataExportId);
            setActiveExport(await getDataExport(result.id));
            const exportResult = await getDataExports(0, 25);
            setExports(exportResult);
        } catch (e) {
            console.error(e);
            setApiError(e);
        }
    };

    const renderExportDetails = () => {
        if (!activeExport) {
            return null;
        }

        return (
            <div>
                <div className="data-export-status-header">
                    <div>
                        <div className="data-export-muted">
                            Started by {getCreatedBy(activeExport)} on {formatDateTime(activeExport.created)}
                        </div>
                        <div className="data-export-muted">
                            Date range: {formatDateRange(activeExport)}
                        </div>
                    </div>
                    <Button id="refresh-data-export"
                        text="Refresh"
                        className="secondary small"
                        onClick={() => refreshExport(activeExport.id)}
                        icon={<i className="ri-refresh-line"></i>}
                        iconLocation="start" />
                </div>
                <div className="data-export-metrics">
                    <div><span>Status</span><strong>{formatStatus(activeExport.status)}</strong></div>
                    <div><span>Processed</span><strong>{activeExport.processedCount}</strong></div>
                    <div><span>Completed</span><strong>{activeExport.successCount}</strong></div>
                    <div><span>Failed</span><strong>{activeExport.failedCount}</strong></div>
                    <div><span>Cancelled</span><strong>{activeExport.cancelledCount || 0}</strong></div>
                </div>
                {ACTIVE_STATUSES.includes(activeExport.status) && <div className="data-export-actions">
                    {activeExport.status !== "CancelRequested" && <Button id="cancel-active-data-export"
                        text="Cancel Export"
                        className="secondary"
                        onClick={() => handleCancelExport(activeExport.id)} />}
                    {activeExport.status === "CancelRequested" && <Button id="force-cancel-active-data-export"
                        text="Force Cancel"
                        className="secondary"
                        onClick={() => handleForceCancelExport(activeExport.id)} />}
                </div>}
                {activeExport.downloadUrl && <div className="data-export-download-row">
                    <Button id="download-final-data-export"
                        className="action"
                        text="Download ZIP"
                        href={activeExport.downloadUrl}
                        newWindow={true}
                        icon={<i className="ri-download-line"></i>}
                        iconLocation="start" />
                    <div className="data-export-muted">
                        {activeExport.fileSize ? `ZIP size: ${formatFileSize(activeExport.fileSize)}` : "ZIP archive ready"}
                    </div>
                </div>}
                {activeExport.errorMessage && <ErrorMessages messages={[activeExport.errorMessage]} />}
                {activeExport.datasets && activeExport.datasets.length > 0 && <div className="data-export-table-scroll">
                    <DataTable
                        columns={[
                            { key: 'file', label: 'CSV File', sortable: true },
                            { key: 'status', label: 'Status', sortable: true },
                            { key: 'recordCount', label: 'Rows', sortable: true },
                            { key: 'fileSize', label: 'File Size', sortable: true },
                            { key: 'completed', label: 'Completed', sortable: true }
                        ] as DataTableColumn[]}
                        data={sortedActiveDatasets}
                        sortBy={datasetSortBy}
                        sortDirection={datasetSortDirection}
                        onSort={onDatasetSort}
                        sortingEnabled={sortableDataExportsEnabled}
                        renderRow={dataset => <tr key={dataset.id} className="table-row">
                                <td>
                                    <strong>{dataset.datasetId}.csv</strong>
                                    <div className="data-export-muted">{dataset.datasetName}</div>
                                    {dataset.errorMessage && <div className="data-export-error">{dataset.errorMessage}</div>}
                                </td>
                                <td>{formatStatus(dataset.status)}</td>
                                <td>{dataset.recordCount}</td>
                                <td>{formatFileSize(dataset.fileSize)}</td>
                                <td>{formatDateTime(dataset.completed, "")}</td>
                            </tr>} />
                </div>}
            </div>
        );
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <section id="main-content" tabIndex={-1}>
            <h1>Data Exports</h1>
            <div className="data-export-shell">
                <ApiErrorMessages error={apiError} />
                {validationError && <ErrorMessages messages={[validationError]} />}
                <div className="data-export-running-toggle-row">
                    <Button id="toggle-running-data-exports"
                        className="secondary"
                        text={`${showRunningExports && hasRunningExports ? "Hide" : "Show"} Running Exports (${runningExports.length})`}
                        disabled={!hasRunningExports}
                        onClick={() => setShowRunningExports(!showRunningExports)}
                        icon={<i className={showRunningExports ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>}
                        iconLocation="end" />
                </div>
                {showRunningExports && hasRunningExports && <div className="data-export-running-panel">
                    <div className="data-export-running-header">
                        <h2>Running Exports</h2>
                        <div className="data-export-header-actions">
                            <Button id="refresh-running-data-exports"
                                className="secondary small"
                                text="Refresh"
                                onClick={refreshExports}
                                icon={<i className="ri-refresh-line"></i>}
                                iconLocation="start" />
                        </div>
                    </div>
                    <div className="data-export-table-scroll">
                        <DataTable
                            columns={[
                                { key: 'created', label: 'Created', sortable: true },
                                { key: 'ranBy', label: 'Ran By', sortable: true },
                                { key: 'status', label: 'Status', sortable: true },
                                { key: 'processedCount', label: 'Progress', sortable: true },
                                { key: 'successCount', label: 'Completed', sortable: true },
                                { key: 'failedCount', label: 'Failed', sortable: true },
                                { key: 'cancelledCount', label: 'Cancelled', sortable: true },
                                { key: 'actions', label: '', sortable: false }
                            ] as DataTableColumn[]}
                            data={sortedRunningExports}
                            sortBy={runningSortBy}
                            sortDirection={runningSortDirection}
                            onSort={onRunningSort}
                            sortingEnabled={sortableDataExportsEnabled}
                            renderRow={dataExport => <tr key={dataExport.id} className="table-row">
                                    <td>{formatDateTime(dataExport.created)}</td>
                                    <td>{getCreatedBy(dataExport)}</td>
                                    <td>{formatStatus(dataExport.status)}</td>
                                    <td>{dataExport.processedCount} of {dataExport.selectedDatasets.length} files</td>
                                    <td>{dataExport.successCount}</td>
                                    <td>{dataExport.failedCount}</td>
                                    <td>{dataExport.cancelledCount || 0}</td>
                                    <td>
                                        <div className="data-export-row-actions">
                                            <Button id={`view-running-data-export-${dataExport.id}`}
                                                text="View"
                                                className="secondary small"
                                                onClick={() => refreshExport(dataExport.id, true)} />
                                            {dataExport.status !== "CancelRequested" && <Button id={`cancel-running-data-export-${dataExport.id}`}
                                                text="Cancel"
                                                className="secondary small"
                                                onClick={() => handleCancelExport(dataExport.id)} />}
                                            {dataExport.status === "CancelRequested" && <Button id={`force-cancel-running-data-export-${dataExport.id}`}
                                                text="Force Cancel"
                                                className="secondary small"
                                                onClick={() => handleForceCancelExport(dataExport.id)} />}
                                        </div>
                                    </td>
                                </tr>} />
                    </div>
                </div>}
                <div className="data-export-running-toggle-row">
                    <Button id="toggle-recent-completed-data-exports"
                        className="secondary"
                        text={`${showRecentCompletedExports && hasRecentCompletedExports ? "Hide" : "Show"} Recently Completed Exports (${recentCompletedExports.length})`}
                        disabled={!hasRecentCompletedExports}
                        onClick={() => setShowRecentCompletedExports(!showRecentCompletedExports)}
                        icon={<i className={showRecentCompletedExports ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>}
                        iconLocation="end" />
                </div>
                {showRecentCompletedExports && hasRecentCompletedExports && <div className="data-export-recent-panel">
                    <div className="data-export-running-header">
                        <h2>Recently Completed Exports</h2>
                        <div className="data-export-header-actions">
                            <Button id="refresh-recent-data-exports"
                                className="secondary small"
                                text="Refresh"
                                onClick={refreshExports}
                                icon={<i className="ri-refresh-line"></i>}
                                iconLocation="start" />
                        </div>
                    </div>
                    <div className="data-export-table-scroll">
                        <DataTable
                            columns={[
                                { key: 'completed', label: 'Finished', sortable: true },
                                { key: 'ranBy', label: 'Ran By', sortable: true },
                                { key: 'status', label: 'Status', sortable: true },
                                { key: 'dateRange', label: 'Date Range', sortable: false },
                                { key: 'files', label: 'Files', sortable: true },
                                { key: 'fileSize', label: 'File Size', sortable: true },
                                { key: 'actions', label: '', sortable: false }
                            ] as DataTableColumn[]}
                            data={sortedRecentExports}
                            sortBy={recentSortBy}
                            sortDirection={recentSortDirection}
                            onSort={onRecentSort}
                            sortingEnabled={sortableDataExportsEnabled}
                            renderRow={dataExport => <tr key={dataExport.id} className="table-row">
                                    <td>{formatDateTime(dataExport.completed)}</td>
                                    <td>{getCreatedBy(dataExport)}</td>
                                    <td>{formatStatus(dataExport.status)}</td>
                                    <td>{formatDateRange(dataExport)}</td>
                                    <td>{dataExport.selectedDatasets.length}</td>
                                    <td>{formatFileSize(dataExport.fileSize)}</td>
                                    <td>
                                        <div className="data-export-row-actions">
                                            {dataExport.downloadUrl && <Button id={`download-recent-data-export-${dataExport.id}`}
                                                text="Download"
                                                className="action small"
                                                href={dataExport.downloadUrl}
                                                newWindow={true}
                                                icon={<i className="ri-download-line"></i>}
                                                iconLocation="start" />}
                                            <Button id={`view-recent-data-export-${dataExport.id}`}
                                                text="View"
                                                className="secondary small"
                                                onClick={() => refreshExport(dataExport.id, true)} />
                                        </div>
                                    </td>
                                </tr>} />
                    </div>
                </div>}
                <div className="data-export-header-row">
                    <div>
                        <h2>Export Data</h2>
                        <div className="data-export-muted">
                            Select data packages to include in one ZIP. Related records and reference values are consolidated into readable CSV files.
                        </div>
                    </div>
                    <div className="data-export-header-actions">
                        <Button id="select-all-data-export-datasets"
                            text="Select All"
                            className="secondary small"
                            disabled={activeExportRunning || submitting || datasets.length === selectedDatasetIds.length}
                            onClick={selectAllDatasets} />
                        <Button id="clear-data-export-datasets"
                            text="Clear"
                            className="secondary small"
                            disabled={activeExportRunning || submitting || selectedDatasetIds.length === 0}
                            onClick={clearDatasets} />
                    </div>
                </div>
                <div className="data-export-date-range">
                    <Input id="data-export-date-range-start"
                        label="Created/Updated From"
                        type="date"
                        value={dateRangeStart}
                        disabled={activeExportRunning || submitting}
                        onChange={(value) => {
                            setValidationError(undefined);
                            setDateRangeStart(value);
                        }} />
                    <Input id="data-export-date-range-end"
                        label="Created/Updated To"
                        type="date"
                        value={dateRangeEnd}
                        disabled={activeExportRunning || submitting}
                        onChange={(value) => {
                            setValidationError(undefined);
                            setDateRangeEnd(value);
                        }} />
                </div>
                <div className="data-export-dataset-grid">
                    {datasets.map(dataset => <div className="data-export-dataset" key={dataset.id}>
                        <div className="data-export-dataset-title-row">
                            <Checkbox id={`data-export-dataset-${dataset.id}`}
                                checked={selectedDatasetSet.has(dataset.id)}
                                disabled={activeExportRunning || submitting}
                                onChange={(checked) => toggleDataset(dataset.id, checked)}
                                label={dataset.name} />
                            <Button id={`data-export-dataset-details-${dataset.id}`}
                                text="Details"
                                className="secondary small"
                                onClick={() => setDatasetDetails(dataset)} />
                        </div>
                        <div className="data-export-dataset-description">{dataset.description}</div>
                        <div className="data-export-dataset-file-count">
                            {getIncludedExportFiles(dataset).length} {getIncludedExportFiles(dataset).length === 1 ? "CSV file" : "CSV files"}
                        </div>
                    </div>)}
                </div>
                <div className="data-export-actions">
                    <Button id="create-data-export"
                        text={activeExportRunning ? "Export Running" : "Start Export"}
                        className="action"
                        loading={submitting}
                        disabled={activeExportRunning || submitting || selectedDatasetIds.length === 0}
                        onClick={handleOpenConfirmExport} />
                    <Button id="view-data-export-history"
                        text="View Export History"
                        className="secondary"
                        href="/admin/data-exports/history" />
                    {activeExportRunning && <div className="data-export-muted">
                        One export can run at a time for the organization.
                    </div>}
                </div>
            </div>
            <Modal id="confirm-data-export-modal"
                visible={confirmExportVisible}
                className="small"
                onClose={() => setConfirmExportVisible(false)}
                title="Confirm Export">
                <div>
                    <div className="data-export-confirm-summary">
                        <div><span>Data Packages</span><strong>{selectedDatasetIds.length}</strong></div>
                        <div><span>CSV Files</span><strong>{selectedFileCount}</strong></div>
                        <div><span>Date Range</span><strong>{dateRangeStart || dateRangeEnd ? `${dateRangeStart || "Beginning"} to ${dateRangeEnd || "Now"}` : "All Dates"}</strong></div>
                    </div>
                    {selectedDatasets.length > 0 && <div className="data-export-confirm-datasets">
                        {selectedDatasets.map(dataset => <div key={dataset.id} className="data-export-confirm-dataset">
                            <strong>{dataset.name}</strong>
                            <span>{getIncludedExportFiles(dataset).map(file => file.fileName).join(", ")}</span>
                        </div>)}
                    </div>}
                    <div className="row justify-content-between margin-top-5">
                        <div className="column-auto">
                            <Button id="cancel-data-export"
                                text="Cancel"
                                className="secondary"
                                disabled={submitting}
                                onClick={() => setConfirmExportVisible(false)} />
                        </div>
                        <div className="column-auto">
                            <Button id="confirm-data-export"
                                text="Start Export"
                                className="action"
                                loading={submitting}
                                disabled={submitting}
                                onClick={handleCreateExport} />
                        </div>
                    </div>
                </div>
            </Modal>
            <Modal id="data-export-details-modal"
                visible={exportDetailsVisible && activeExport !== undefined}
                className="medium"
                onClose={() => setExportDetailsVisible(false)}
                title="Export Details">
                {renderExportDetails()}
            </Modal>
            <Modal id="data-export-dataset-details-modal"
                visible={datasetDetails !== undefined}
                className="small"
                onClose={() => setDatasetDetails(undefined)}
                title={datasetDetails?.name || "Export Details"}>
                <div>
                    <div className="data-export-muted">{datasetDetails?.description}</div>
                    {datasetDetails?.includedDatasets && datasetDetails.includedDatasets.length > 0 && <div className="data-export-included-list">
                        {datasetDetails.includedDatasets.map(dataset => <div key={dataset.id} className="data-export-included-item">
                            <div className="data-export-included-heading">
                                <strong>{dataset.name}</strong>
                                <span>{dataset.fileName}</span>
                            </div>
                            <div className="data-export-muted">{dataset.description}</div>
                            {dataset.details && dataset.details.length > 0 && <ul className="data-export-detail-list">
                                {dataset.details.map(detail => <li key={detail}>{detail}</li>)}
                            </ul>}
                        </div>)}
                    </div>}
                    <div className="row justify-content-end margin-top-5">
                        <div className="column-auto">
                            <Button id="close-data-export-dataset-details"
                                text="Close"
                                className="secondary"
                                onClick={() => setDatasetDetails(undefined)} />
                        </div>
                    </div>
                </div>
            </Modal>
        </section>
    );
};

export default ManageDataExports;
